import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import FilterDropdownMC from '../../components/FilterDropdownMC';
import { CheckedIconMC } from './components/index';
import useQuickFiltersUI from '../../hooks/inbox/ui/useQuickFiltersUI';
import DropdownButton from '../../components/DropdownButtonMC';
import ButtonComponent from '../../components/ButtonMC';

/**
 * InboxQuickFiltersMC - Molecule Component (Representational)
 *
 * Component for quick filters of activity and status in the inbox
 * Separates total business logic (useQuickFiltersUI) and presentation
 *
 * @param {Object} filter - Current filter state (includes quickFilters)
 * @param {function} onFilterChange - Handler for quick filter changes
 */

const InboxQuickFiltersMC = memo(({
  filter = {},
  onFilterChange,
  isSelectingConversations = false,
  selectedConversations,
  toggleSelectingConversations = () => { },
  exitSelectingConversations = () => { }
}) => {
  const { t } = useTranslation('InboxMC');
  // Hook that encapsulates ALL business logic (except selection)
  const {
    activityOptions,
    activitySelectOptions,
    closeSelectedTitle,
    handleActivityToggle,
    handleStatusToggle,
    selectedActivityFilters,
    selectedStatusFilters,
    statusOptions,
    showBulkAssignToSelf,
    handleBulkAssignToSelf
  } = useQuickFiltersUI(filter, onFilterChange, selectedConversations, exitSelectingConversations);

  return (
    <div className="tw-w-full tw-mt-2">
      <div className="tw-flex tw-flex-row tw-justify-start tw-items-center tw-gap-1 tw-w-full tw-h-6">

        <CheckedIconMC
          checked={isSelectingConversations}
          onToggle={toggleSelectingConversations}
        />

        {isSelectingConversations && (
          <>
            <div className="tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-1">
              <DropdownButton
                variant="GRAY"
                text={t('filters.activity.label')}
                items={activitySelectOptions}
                containerClassName="tw-min-w-0 tw-flex-1"
                className="!tw-border !tw-border-gray-300 !tw-bg-gray-50 !tw-text-gray-600 !tw-text-xs !tw-font-medium !tw-px-3 !tw-py-1 tw-w-full tw-justify-between"
                dropdownClassName="tw-z-50"
              />
              {showBulkAssignToSelf && (
                <ButtonComponent
                  variant="SECONDARY"
                  size="xs"
                  text={t('header.assign_to_me')}
                  onClick={handleBulkAssignToSelf}
                  className="tw-shrink-0 tw-whitespace-nowrap !tw-px-2 !tw-py-1 tw-text-2xs"
                />
              )}
            </div>
            <div className="tw-flex-1 tw-min-w-0" />
            <ButtonComponent
              variant="SECONDARY"
              size="xs"
              icon="close-outline"
              outlined
              onClick={toggleSelectingConversations}
              className="tw-border-0 tw-bg-transparent hover:tw-bg-transparent !tw-pt-1"
              iconClassName="tw-text-gray-600 !tw-size-[24px]"
              title={closeSelectedTitle}
            />
          </>
        )}
        {!isSelectingConversations && (
          <>
            <div className="tw-flex-1 tw-min-w-0">
              <FilterDropdownMC
                options={activityOptions}
                selectedValues={selectedActivityFilters}
                onItemToggle={handleActivityToggle}
                placeholder={t('filters.activity.label')}
              />
            </div>

            <div className="tw-flex-1 tw-min-w-0">
              <FilterDropdownMC
                showRedDot={selectedStatusFilters.find((o) => o === 'pending') === undefined}
                options={statusOptions}
                selectedValues={selectedStatusFilters}
                onItemToggle={handleStatusToggle}
                placeholder={statusOptions.find((o) => o.id === selectedStatusFilters[0])?.label || t('filters.status.label')}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
});

InboxQuickFiltersMC.displayName = 'InboxQuickFiltersMC';

export default InboxQuickFiltersMC;
